import type {
  Lesson,
  RepoState,
  QuizQuestion,
  GlossaryItem,
  CheatsheetItem,
  ConflictHunk,
} from "../../lib/types";
import type { ConflictScenario } from "../../lib/git-sim";

export const LESSON_MERGE_CONFLICTS: Lesson = {
  courseId: "merge-conflicts",
  courseTitle: "Merge conflicts, calmly",
  id: "lesson-1",
  title: "Merge conflicts, calmly",
  subtitle: "Lesson 5 of 6",
  steps: [
    {
      id: "intro",
      shortTitle: "Why conflicts happen",
      shortSub: "Two people, one line",
      eyebrow: "Step 1 of 5",
      title: "Conflicts aren't errors — they're git asking for your opinion.",
      sub: "When two branches change the same line of the same file, git refuses to guess which version wins. It writes both into the file with marker lines and waits for you to pick. That's all a conflict really is.",
      kind: "intro",
    },
    {
      id: "trigger",
      shortTitle: "Trigger the conflict",
      shortSub: "Make the merge fail",
      eyebrow: "Step 2 of 5",
      title: "You're on main. A teammate edited the same line on feature.",
      sub: "Both branches have diverged from the same starting point. Try merging feature into main — git will hit the same line twice and stop with a conflict. Then look at App.jsx in the editor and the terminal.",
      kind: "conflict-sandbox",
      goal: "trigger-conflict",
    },
    {
      id: "anatomy",
      shortTitle: "Read the markers",
      shortSub: "<<<, ===, >>>",
      eyebrow: "Step 3 of 5",
      title: "The conflict markers, in plain English.",
      sub: "Git's marker syntax looks scarier than it is. There are exactly three lines that matter — <<<<<<<, =======, and >>>>>>>. Everything between them is two versions of the same lines, stacked on top of each other.",
      kind: "conflict-anatomy",
    },
    {
      id: "resolve",
      shortTitle: "Resolve & commit",
      shortSub: "Pick a winner, finish the merge",
      eyebrow: "Step 4 of 5",
      title: "Pick a resolution for each hunk, then commit.",
      sub: "Resolve every conflict hunk (use the panel on the right), then commit. Git creates a special merge commit with two parents — one from main, one from feature — and the merge is done.",
      kind: "conflict-sandbox",
      goal: "resolve-conflict",
    },
    {
      id: "wrap",
      shortTitle: "Quiz & recap",
      shortSub: "Knowledge check",
      eyebrow: "Wrap-up",
      title: "Conflict → resolve → merge commit.",
      sub: "That's the whole loop. Quick check below, then cheatsheet + glossary.",
      kind: "wrap",
    },
  ],
};


export const QUIZ_QUESTIONS_MERGE_CONFLICTS: QuizQuestion[] = [
  {
    q: "Why does git create a conflict instead of just merging?",
    options: [
      { t: "Because the repo is corrupted", correct: false },
      {
        t: "Because two branches changed the same line and git won't guess which to keep",
        correct: true,
      },
      { t: "Because one branch is behind the other", correct: false },
      { t: "Because the merge command was typed wrong", correct: false },
    ],
    why: "Exactly. A conflict is git asking for a decision — same line, two versions, no automatic winner.",
    wrongWhy:
      "A conflict isn't an error. Git can usually merge automatically — when it can't (because both sides edited the same line) it stops and shows both versions so a human can choose.",
  },
  {
    q: "In a conflict file you see `<<<<<<< HEAD` then your text, then `=======`, then more text, then `>>>>>>> feature`. Which is yours?",
    options: [
      { t: "The text between `=======` and `>>>>>>>`", correct: false },
      { t: "The text between `<<<<<<< HEAD` and `=======`", correct: true },
      { t: "Both — git always duplicates", correct: false },
      { t: "Neither — the markers replaced your code", correct: false },
    ],
    why: "Yes. `<<<<<<< HEAD` is 'the branch you're on' (ours). After `=======` is 'theirs' — the branch you asked git to merge in.",
    wrongWhy:
      "`<<<<<<< HEAD` marks the start of *your* branch's version. Everything until `=======` is yours; everything between `=======` and `>>>>>>>` is the other branch's version.",
  },
  {
    q: "You're in the middle of a conflict and want to back out completely — pretend you never tried to merge. What do you run?",
    options: [
      { t: "git reset --hard", correct: false },
      { t: "git merge --abort", correct: true },
      { t: "git revert", correct: false },
      { t: "Delete the markers manually and commit", correct: false },
    ],
    why: "Right — `git merge --abort` cleanly restores your branch to the state before the merge attempt. The simulator's Reset button does the same thing.",
    wrongWhy:
      "`git merge --abort` is the safe escape hatch. It undoes the in-progress merge and puts your files back exactly the way they were. The other options either lose work or aren't the right tool here.",
  },
  {
    q: "After you resolve a conflict and `git commit`, what's special about the commit git creates?",
    options: [
      { t: "Nothing — it's a regular commit", correct: false },
      { t: "It's a 'merge commit' with two parents", correct: true },
      { t: "It's marked as a tag", correct: false },
      { t: "It overwrites the previous commit on main", correct: false },
    ],
    why: "Exactly. A merge commit points back to both branches' tips — one parent per branch. That's how the history records 'this is where these two lines of work joined up.'",
    wrongWhy:
      "A merge commit is special — it has two parent commits, one from each branch that got merged. That's what lets git history visualize the merge as a join in the graph.",
  },
];

export const GLOSSARY_MERGE_CONFLICTS: GlossaryItem[] = [
  {
    term: "Merge conflict",
    def: "What happens when two branches changed the same line of the same file and git can't pick a winner.",
  },
  {
    term: "Conflict marker",
    def: "The `<<<<<<<`, `=======`, `>>>>>>>` lines git writes into a file to show both versions side by side.",
  },
  { term: "Ours", def: "The version of the conflict from the branch you're currently on (HEAD)." },
  { term: "Theirs", def: "The version from the branch you asked git to merge in." },
  {
    term: "Hunk",
    def: "One conflicting region of a file. A single file can have several hunks if changes overlapped in multiple places.",
  },
  {
    term: "Merge commit",
    def: "A commit with two parents — one from each branch — that records where work was joined. Made automatically when you commit after resolving a conflict.",
  },
  {
    term: "git merge --abort",
    def: "Bails out of an in-progress merge and restores files to the way they were before you tried.",
  },
  {
    term: "Three-way merge",
    def: "Git's underlying algorithm: it compares both branches against their shared ancestor to decide what to keep. Conflicts happen where the algorithm sees both sides change the same lines.",
  },
];

export const CHEATSHEET_MERGE_CONFLICTS: CheatsheetItem[] = [
  { cmd: "git merge <branch>", desc: "Try to merge — may succeed cleanly or stop with conflicts." },
  { cmd: "git status", desc: "Lists which files are conflicted (marked 'both modified')." },
  { cmd: "Edit conflicted files", desc: "Remove the `<<<<`, `====`, `>>>>` markers and leave the final version you want." },
  { cmd: "git add <file>", desc: "Tell git this file's conflict is resolved." },
  { cmd: "git commit", desc: "Finish the merge — creates a merge commit with two parents." },
  { cmd: "git merge --abort", desc: "Back out of an in-progress merge. Files restore to pre-merge state." },
  { cmd: "git diff", desc: "While conflicted, shows the conflict markers in context — useful for triage." },
];

// ----------------- Initial repo state -----------------

/**
 * Shared base App.jsx (commit "c7d8e9f" — the divergence point).
 * Both main and feature branched off this version.
 */
const APP_JSX_BASE = `import Header from './Header';

export default function App() {
  return (
    <div className="app">
      <Header />
      <main>
        <h1>Welcome to Atlas</h1>
        <p>Your Business Operating System.</p>
      </main>
    </div>
  );
}
`;

/** App.jsx on main's tip — the learner is here. */
const APP_JSX_MAIN = `import Header from './Header';

export default function App() {
  return (
    <div className="app">
      <Header />
      <main>
        <h1>The Atlas operating system</h1>
        <p>The single workspace your team actually opens every morning.</p>
      </main>
    </div>
  );
}
`;

/** App.jsx on feature's tip — what the teammate wrote. */
const APP_JSX_FEATURE = `import Header from './Header';

export default function App() {
  return (
    <div className="app">
      <Header />
      <main>
        <h1>Atlas — your team's BOS</h1>
        <p>Built for ops teams who care about velocity.</p>
      </main>
    </div>
  );
}
`;

const HEADER_JSX = `export default function Header() {
  return (
    <header className="header">
      <img src="/logo.png" alt="logo" />
      <nav>
        <a href="/">Home</a>
        <a href="/about">About</a>
      </nav>
    </header>
  );
}
`;

const README_MD = `# Atlas

Internal business operating system.

Built by the team at Nymbl.
`;

const FILE_PREAMBLE = `import Header from './Header';

export default function App() {
  return (
    <div className="app">
      <Header />
      <main>
`;
const FILE_SUFFIX = `      </main>
    </div>
  );
}
`;

const HUNK_OURS = `        <h1>The Atlas operating system</h1>
        <p>The single workspace your team actually opens every morning.</p>
`;
const HUNK_THEIRS = `        <h1>Atlas — your team's BOS</h1>
        <p>Built for ops teams who care about velocity.</p>
`;

/**
 * The conflict scenario triggered by 'git merge feature' on main.
 * One hunk covering the heading + tagline.
 */
export const CONFLICT_SCENARIO: ConflictScenario = {
  file: "App.jsx",
  literals: [FILE_PREAMBLE, FILE_SUFFIX],
  hunks: [
    {
      oursLabel: "HEAD (main)",
      theirsLabel: "feature",
      ours: HUNK_OURS,
      theirs: HUNK_THEIRS,
      resolution: null,
    } as ConflictHunk,
  ],
};

/**
 * Starting state for the merge-conflicts lesson.
 *
 * Both `main` and `feature` have one commit past the shared `c7d8e9f` base,
 * editing the same lines in App.jsx. Learner is on main.
 */
export const INITIAL_REPO_MERGE_CONFLICTS: RepoState = {
  commits: [
    { id: "a1b2c3d", msg: "Initial commit", branch: "main", parent: null, x: 0 },
    { id: "e4f5a6b", msg: "Add Header component", branch: "main", parent: "a1b2c3d", x: 1 },
    { id: "c7d8e9f", msg: "Wire up homepage layout", branch: "main", parent: "e4f5a6b", x: 2 },
    {
      id: "m1a2in3",
      msg: "Sharpen homepage copy",
      branch: "main",
      parent: "c7d8e9f",
      x: 3,
    },
    {
      id: "f1e2at3",
      msg: "Rewrite hero for ops teams",
      branch: "feature",
      parent: "c7d8e9f",
      x: 3,
    },
  ],
  branches: { main: "m1a2in3", feature: "f1e2at3" },
  head: "main",
  files: {
    "App.jsx": {
      content: APP_JSX_MAIN,
      committed: APP_JSX_MAIN,
      staged: null,
      status: "clean",
    },
    "Header.jsx": {
      content: HEADER_JSX,
      committed: HEADER_JSX,
      staged: null,
      status: "clean",
    },
    "README.md": {
      content: README_MD,
      committed: README_MD,
      staged: null,
      status: "clean",
    },
  },
  conflict: null,
  pendingMerge: null,
};

/**
 * Map of branch → file content at that branch's tip. Used by the lesson screen
 * so the editor can show "the version on this branch" when the learner switches
 * branches in the trigger step (read-only sneak preview, before the merge).
 */
export const BRANCH_TIP_APP_JSX: Record<string, string> = {
  main: APP_JSX_MAIN,
  feature: APP_JSX_FEATURE,
  base: APP_JSX_BASE,
};

export const INITIAL_LOG_MERGE_CONFLICTS = [
  {
    kind: "out" as const,
    text: "On branch main. Your teammate pushed to feature — both branches changed the homepage copy.",
  },
];
